/**
 * Muscle load — absolute fractional set counts per muscle group, for one day and for the week.
 *
 * **This is the one computation in the plan builder with no backend fixture behind it**, and the
 * execution ADR §5 says so explicitly. The backend's `muscleSplit` computes a different thing:
 * integer PERCENTAGES over the full fourteen-group taxonomy. The v3 canon needs absolute
 * FRACTIONAL set counts over six display groups, alongside a percentage. Neither can be derived
 * from the other, so this is computed web-side and the ADR records it as "the one conscious
 * exception to zero new logic".
 *
 * Three things stand in for the missing fixture, and all three are tests in this slice:
 *   · every group's day sums equal its week sum — the marquee invariant, and the reason there is
 *     one computation below rather than two;
 *   · an exhaustive pass over all fourteen backend groups, so a fifteenth goes red rather than
 *     silently vanishing;
 *   · the `FullBody` / `Other` bucketing is asserted explicitly rather than assumed.
 *
 * A backend-emitted fixture is the correct long-term answer and is recorded in the ADR as a
 * follow-up. Until it exists, the day-equals-week invariant is a strong guard but it is not
 * parity: both sides could be wrong together.
 *
 * ── The scoring rule ─────────────────────────────────────────────────────────────────────────
 * Each exercise contributes `countingSets × 1.0` to every PRIMARY group it names and
 * `countingSets × 0.5` to every SECONDARY one. "Counting sets" is `setCounting.ts`'s rule — a set
 * counts unless it is a warm-up — and a warm-up-ROLE exercise contributes nothing at all, which is
 * what the canon's "poza licznikiem serii" says.
 *
 * Halves are never rounded away. The canon prints "11,5" and "14,5 pkt", and the half is exactly
 * the information a trainer reads a secondary contribution off: rounding it would make an
 * assistance exercise indistinguishable from a primary one.
 *
 * ── Attribution arrives as a PARAMETER ───────────────────────────────────────────────────────
 * Which groups an exercise trains lives in `entities/exercise-library`, and entities may not import
 * each other (execution ADR §2). The page composes the two. That is also what makes this module a
 * pure function of its inputs and testable with no library fixture at all.
 *
 * The map is keyed on the wire's own PascalCase strings (`"Chest"`, `"FullBody"`) rather than on a
 * parsed enum, for the same reason: parsing them is the library's job, and a shared enum would be
 * the entity-to-entity coupling this parameter exists to avoid.
 *
 * ── The one import, and why `model/` may make it ─────────────────────────────────────────────
 * `shared/ui/muscleColour` holds the fourteen-to-six table this module used to own — moved there on
 * 17 September 2026 so `entities/exercise-library`'s row could read it without one entity importing
 * another. It is `ui/` by name because it also names the `--mg-*` tokens, but it is a pure string
 * table: no React, no DOM, no query hook, no i18next. `model/`'s purity rule is untouched.
 */

import { MUSCLE_COLOUR_SOURCE_GROUPS, muscleColourGroupOf } from '~/shared/ui/muscleColour';

/** The six groups the design palette draws, and the six `--mg-*` tokens. */
export type MuscleDisplayGroup = 'chest' | 'back' | 'legs' | 'shoulders' | 'arms' | 'core';

/**
 * The canonical order — the order the week strip draws its six bars in, and the tie-break for
 * everything below. It is the canon's own left-to-right order: Klatka, Plecy, Nogi, Barki,
 * Ramiona, Core.
 */
export const MUSCLE_DISPLAY_GROUPS: readonly MuscleDisplayGroup[] = [
  'chest',
  'back',
  'legs',
  'shoulders',
  'arms',
  'core',
];

/**
 * The fourteen-to-six rollup — **`shared/ui/muscleColour`'s table, read through its own name here.**
 *
 * It lived in this file until 17 September 2026 and moved out whole when the exercise library's row
 * needed the same answer: `entities/` may not import `entities/` (execution ADR §2), so a row in
 * `entities/exercise-library` could not ask this module which bucket a movement falls in, and a
 * second table would be two opinions about one clustering. The reasoning behind the buckets — why
 * `Other` and `FullBody` fold into `core`, and why the record is exhaustive rather than defaulted —
 * travelled with it and is stated there.
 *
 * What stays here is the DOMAIN vocabulary: `MuscleDisplayGroup` is this slice's type, and the two
 * exports below are the names the rest of the app already calls this rollup by.
 *
 * The exhaustive test does NOT iterate `BACKEND_MUSCLE_GROUPS` — it iterates its own literal mirror
 * of the C# enum, so a fifteenth backend group goes red at `displayGroupOf` rather than being
 * silently added to both sides of one comparison. This export is what that test checks the mirror
 * AGAINST.
 */
export const BACKEND_MUSCLE_GROUPS: readonly string[] = MUSCLE_COLOUR_SOURCE_GROUPS;

/** One backend group name → its display bucket, or `null` when this build has never heard of it. */
export function displayGroupOf(backendGroup: string | null | undefined): MuscleDisplayGroup | null {
  return muscleColourGroupOf(backendGroup);
}

/** What one exercise trains, in the wire's own PascalCase group names. */
export interface MuscleAttribution {
  readonly primary: readonly string[];
  readonly secondary: readonly string[];
}

/**
 * Exercise id → what it trains. Supplied by the page from `entities/exercise-library`; an exercise
 * the library read did not return is simply absent, and contributes nothing.
 */
export type MuscleAttributionMap = ReadonlyMap<number, MuscleAttribution>;

/** One exercise's contribution to attribute: which movement, and how many of its sets count. */
export interface CountedExercise {
  readonly exerciseId: number;
  readonly countingSetCount: number;
}

/** One row of a load module: a group, its points, its share, and how long to draw its bar. */
export interface MuscleLoadEntry {
  readonly group: MuscleDisplayGroup;
  /** Absolute fractional set count. Halves are real — never rounded. */
  readonly points: number;
  /** Whole-number share of this scope's total, summing to exactly 100 across the entries. */
  readonly percent: number;
  /**
   * Bar length as a fraction of the LARGEST entry in this scope, 0–1. The canon's bars are
   * relative to the leader, not to the total: Monday's Klatka at 8 of 18 draws full width, and its
   * Barki at 4 draws half. A bar scaled by percentage would make every day's leader a different
   * length and destroy the comparison the module exists for.
   */
  readonly barFraction: number;
}

/** A load module: its rows, and the total they are shares of. */
export interface MuscleLoad {
  readonly entries: readonly MuscleLoadEntry[];
  /** Absolute total points in this scope. The canon's "18 pkt". */
  readonly totalPoints: number;
}

/** The one computation's output: a load per day, in the order the days came in, plus their sum. */
export interface MuscleLoadBreakdown {
  /**
   * One entry per input day, positionally. Rows are non-zero groups in DESCENDING points — the
   * canon's day module lists only what the day actually trains, heaviest first.
   */
  readonly days: readonly MuscleLoad[];
  /**
   * The whole week. Rows are ALL SIX groups in canonical order, including zeroes — the canon's week
   * strip always draws six bars, because a group missing from the week is itself the finding.
   */
  readonly week: MuscleLoad;
}

type PointsByGroup = Map<MuscleDisplayGroup, number>;

/** Accumulate one exercise's contribution into a group→points map. */
function addExercise(
  points: PointsByGroup,
  exercise: CountedExercise,
  attribution: MuscleAttributionMap,
): void {
  const trains = attribution.get(exercise.exerciseId);
  // Not in the library read: contributes NOTHING — no groups, and nothing added to the total. A
  // zero-point entry would claim the exercise trains something at intensity zero, and inflating
  // the total would silently shrink every other group's share.
  if (trains === undefined) return;
  if (exercise.countingSetCount <= 0) return;

  const contribute = (names: readonly string[], weight: number): void => {
    for (const name of names) {
      const group = displayGroupOf(name);
      if (group === null) continue;
      points.set(group, (points.get(group) ?? 0) + exercise.countingSetCount * weight);
    }
  };
  contribute(trains.primary, 1);
  contribute(trains.secondary, 0.5);
}

/**
 * Whole-number percentages that sum to exactly 100, by largest remainder.
 *
 * Naive rounding does not sum to 100 — the canon's own week is the proof: six groups over a total
 * of 48 round to 19 + 21 + 24 + 9 + 14 + 14 = 101. Largest remainder distributes the shortfall to
 * the entries whose fractional parts were largest, which is why the canon prints 13% and 14% for
 * two groups that both hold 6.5 points.
 *
 * **Ties break by canonical group order**, so the same input always produces the same output. Two
 * equal groups genuinely cannot both round the same way, and picking the earlier one is arbitrary
 * but stable — a percentage that flips between renders is worse than one that is arguably on the
 * wrong row.
 */
function largestRemainderPercents(
  ordered: readonly { group: MuscleDisplayGroup; points: number }[],
  totalPoints: number,
): Map<MuscleDisplayGroup, number> {
  const percents = new Map<MuscleDisplayGroup, number>();
  if (totalPoints <= 0) {
    for (const row of ordered) percents.set(row.group, 0);
    return percents;
  }

  const exact = ordered.map((row) => {
    const value = (row.points / totalPoints) * 100;
    const floor = Math.floor(value);
    return { group: row.group, floor, remainder: value - floor };
  });

  let assigned = exact.reduce((sum, row) => sum + row.floor, 0);
  for (const row of exact) percents.set(row.group, row.floor);

  // Hand out the remaining whole points, largest fractional part first, canonical order on a tie.
  const byRemainder = [...exact].sort(
    (a, b) =>
      b.remainder - a.remainder ||
      MUSCLE_DISPLAY_GROUPS.indexOf(a.group) - MUSCLE_DISPLAY_GROUPS.indexOf(b.group),
  );
  for (const row of byRemainder) {
    if (assigned >= 100) break;
    percents.set(row.group, (percents.get(row.group) ?? 0) + 1);
    assigned += 1;
  }
  return percents;
}

/** Turn a group→points map into a rendered load module. */
function toLoad(
  points: PointsByGroup,
  order: 'descending' | 'canonical',
  includeZeroes: boolean,
): MuscleLoad {
  const rows = MUSCLE_DISPLAY_GROUPS.map((group) => ({ group, points: points.get(group) ?? 0 }))
    .filter((row) => includeZeroes || row.points > 0)
    .sort((a, b) =>
      order === 'canonical'
        ? MUSCLE_DISPLAY_GROUPS.indexOf(a.group) - MUSCLE_DISPLAY_GROUPS.indexOf(b.group)
        : b.points - a.points ||
          MUSCLE_DISPLAY_GROUPS.indexOf(a.group) - MUSCLE_DISPLAY_GROUPS.indexOf(b.group),
    );

  const totalPoints = rows.reduce((sum, row) => sum + row.points, 0);
  const maxPoints = rows.reduce((max, row) => Math.max(max, row.points), 0);
  const percents = largestRemainderPercents(rows, totalPoints);

  return {
    totalPoints,
    entries: rows.map((row) => ({
      group: row.group,
      points: row.points,
      percent: percents.get(row.group) ?? 0,
      barFraction: maxPoints > 0 ? row.points / maxPoints : 0,
    })),
  };
}

/**
 * The load of each day, and of the week they make up — **from one computation**.
 *
 * The week is summed from the same per-day point maps the day modules are rendered from, rather
 * than recomputed over the flattened exercise list. That is what makes "every group's day sums
 * equal its week sum" a structural property instead of a coincidence two code paths have to keep
 * agreeing on. It is the invariant the canon's whole load module rests on: a trainer who adds the
 * day figures must get the week figure, or neither number is trustworthy.
 *
 * Percentages are NOT summed — each scope's shares are of its own total, which is why the day and
 * week percentages differ for the same group and both are correct.
 *
 * @param days One list of counted exercises per day, in the order the caller wants them back.
 * @param attribution Which groups each exercise trains. Absent ids contribute nothing.
 */
export function muscleLoadByDay(
  days: readonly (readonly CountedExercise[])[],
  attribution: MuscleAttributionMap,
): MuscleLoadBreakdown {
  const weekPoints: PointsByGroup = new Map();

  const dayLoads = days.map((exercises) => {
    const dayPoints: PointsByGroup = new Map();
    for (const exercise of exercises) addExercise(dayPoints, exercise, attribution);
    for (const [group, value] of dayPoints) {
      weekPoints.set(group, (weekPoints.get(group) ?? 0) + value);
    }
    return toLoad(dayPoints, 'descending', false);
  });

  return { days: dayLoads, week: toLoad(weekPoints, 'canonical', true) };
}
